// Replaces sample-data.js as the new way to work with the dataset in the production website
// For now every part is in here to keep things simple
// Different areas of the api are divided by comment headings


////////////////////////////////////////////////////////////////////////////////
// LEMMATA LIST
////////////////////////////////////////////////////////////////////////////////

// Protection against duplicate api calls
let inFlight_getLemmataList = null; 

export function getLemmataList(setLemmataList, token = '') {
  let url = '/api/lemmata/list';
  const params = new URLSearchParams({token});
  url += '?' + params.toString();
  
  if (!inFlight_getLemmataList) {
    inFlight_getLemmataList = fetch(url)
    .then(response => response.json())
    .then(data => data.map(lemma => {
      lemma.last_edit = new Date(lemma.last_edit);
      return lemma;
    }))
    .then(data => setLemmataList(data))
    .finally(() => { inFlight_getLemmataList = null; });
  }
  return inFlight_getLemmataList;
}

// Streamlines the use of state functions in components
export function getLemmataListPromise(token = '') {
  let url = '/api/lemmata/list';
  const params = new URLSearchParams({token});
  url += '?' + params.toString();

  return new Promise((resolve, reject) => {

    fetch(url, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
      },
      method: "GET",
    })
    .then(response => response.json())
    .then(data => data.map(lemma => {
      lemma.last_edit = new Date(lemma.last_edit);
      return lemma;
    }))
    .then(data => resolve(data))
    .catch(error => reject(error));
  });
}

// Returns a promise so the caller can refresh the lemmata list once the new
// lemma actually exists. Also surfaces a failed request instead of silently
// setting the new id to an error object. – fix
export function addNewLemma(setNewLemmaId, token = '') {
  let url = '/api/lemmata/add';

  // Time out rather than waiting forever, so a server that never replies still
  // tells the user something instead of the button looking dead. – fix
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 20000);

  return fetch(url, {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token,
    },
    method: "GET",
    signal: controller.signal,
  })
  .then(response => {
    // Read the body as text first: an error reply is not always valid JSON,
    // and calling .json() on it throws a confusing parse error instead of
    // showing what the server actually said. – fix
    return response.text().then(text => {
      let body;
      try { body = JSON.parse(text); } catch (e) { body = text; }

      // Keep the raw reply so any failure can report exactly what arrived,
      // instead of a vague "did not return an id". – fix
      const raw = 'status ' + response.status + ' ' + response.statusText
        + '\ncontent-type: ' + (response.headers.get('content-type') || 'none')
        + '\nbody: ' + (text === '' ? '(completely empty)' : text.slice(0, 400));

      if (!response.ok) {
        const detail = (body && body.error)
          ? body.error + (body.detail ? ' (' + body.detail + ')' : '')
          : (text || 'no details');
        throw new Error('Could not add a new lemma.\n\nServer replied ' + response.status + ':\n' + detail);
      }
      return { body, raw };
    });
  })
  .then(({ body, raw }) => {
    const newLemmaId = body;
    if (!newLemmaId && newLemmaId !== 0) {
      throw new Error('The server did not return an id for the new lemma.\n\n'
        + 'What the server actually sent:\n' + raw);
    }
    setNewLemmaId(newLemmaId);
    return newLemmaId;
  })
  .catch(error => {
    console.error('addNewLemma failed:', error);
    alert(error.name === 'AbortError'
      ? 'Could not add a new lemma: the server never answered (timed out after 20 seconds).'
      : error.message);
  })
  .finally(() => clearTimeout(timeoutId));
}

////////////////////////////////////////////////////////////////////////////////
// LEMMA
////////////////////////////////////////////////////////////////////////////////

// Protection against duplicate api calls
let inFlight_getLemmaFromDB = null; 

export function getLemmaFromDB(setLemma, lemmaId) {

  // Skip the fetch process if the lemmaId is missing
  // React Router can put the literal string "null" in the URL, which is not a
  // real id — treat it the same as missing instead of asking the server. – fix
  if (!lemmaId || lemmaId === 'null' || lemmaId === 'undefined') {
    setLemma();
    return;
  }

  let url = '/api/lemma/get';
  const params = new URLSearchParams({lemmaId});
  url += '?' + params.toString();

  if (!inFlight_getLemmaFromDB) {
    // Time the request out after 20s. Without this, a request that never gets a
    // response leaves inFlight_getLemmaFromDB set forever, and every later call
    // returns that dead promise — so no lemma ever loads again until reload. – fix
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);

    inFlight_getLemmaFromDB = fetch(url, { signal: controller.signal })
    .then(response => {
      // Make sure that the lemmaId is valid, or set lemma to undefined
      // Needed to prevent panic in Lemma subparts when the id is invalid
      if (response.ok) {
        return response.json();
      } else {
        setLemma();
      }
    })
    .then(data => setLemma(data))
    .catch(error => {
      setLemma();
      console.error(error);
    })
    .finally(() => {
      clearTimeout(timeoutId);
      inFlight_getLemmaFromDB = null;
    });
  }
  return inFlight_getLemmaFromDB;
}

export function saveLemmaToDB(setLemma, lemma, token = '') {
  let url = '/api/lemma/save';

  return fetch(url, {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token,
    },
    method: "PATCH",
    body: JSON.stringify(lemma),
  })
  // The old version called response.json() on every reply, so an error page
  // (e.g. 401 "User not authenticated") was parsed as if it were the saved
  // lemma and quietly replaced the real one — the save appeared to do nothing.
  // Now a failed save says so and leaves the lemma on screen untouched. – fix
  .then(response => {
    if (!response.ok) {
      throw new Error('Save failed (server said ' + response.status + '). '
        + (response.status === 401
          ? 'Your login has expired — please log in again. Your text is still on screen.'
          : 'Your text is still on screen; try saving again.'));
    }
    return response.json();
  })
  .then(data => setLemma(data))
  .catch(error => {
    console.error(error);
    alert(error.message || 'Save failed. Your text is still on screen; try saving again.');
  });
}

export function deleteLemmaFromDB(lemmaId, token = '') {
  let url = '/api/lemma/delete';
  const params = new URLSearchParams({lemmaId});
  url += '?' + params.toString();

  fetch(url, {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token,
    },
    method: "DELETE",
  })
  .then(response => response.json())
  .catch(data => console.log(data));
}

export function checkLemma(lemmaId, checked = false, field = "checked", token = '') {
  // console.log('checkLemma()', lemmaId, checked);
  
  let url = '/api/lemma/check';
  
  fetch(url, {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token,
    },
    method: "PATCH",
    body: JSON.stringify({lemmaId, checked, field}),
  })
  .then(response => response.json())
  .catch(data => console.error(data)); // Add error handling that will show the lemma as unsaved if the operation fails
}

export function getEditHistory(lemmaId, token = '') {
  let url = '/api/lemma/history';
  const params = new URLSearchParams({lemmaId});
  url += '?' + params.toString();

  return new Promise((resolve, reject) => {
    fetch(url, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': (token ? 'Bearer ' + token : ''),
      },
      method: "GET",
    })
    .then(response => response.json())
    .then(data => data.map(edit => {
      edit.timestamp = new Date(edit.timestamp);
      return edit;
    }))
    .then(data => resolve(data))
    .catch(error => reject(error));
  })
}


// Basically mirrors getLemmataListPromise(), but returns something slightly different
// Need a separate function to avoid bogging down the main uses of getLemmataListPromise()
export function getRecentsListPromise(token = '') {
  let url = '/api/recents/list';
  const params = new URLSearchParams({token});
  url += '?' + params.toString();

  return new Promise((resolve, reject) => {
    fetch(url, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
      },
      method: "GET",
    })
    .then(response => response.json())
    .then(data => data.map(lemma => {
      lemma.timestamp = new Date(lemma.timestamp);
      lemma.last_edit = new Date(lemma.last_edit);
      return lemma;
    }))
    .then(data => resolve(data))
    .catch(error => reject(error));
  });
}

export function getCrosslinks(setCrosslinks, token = '') {
  let url = '/api/crosslinks/list';
  fetch(url)
  .then(response => response.json())
  .then(data => setCrosslinks(data));
}

// Change token default to null after adding authentication
export function runAdvancedSearchDB(whereComponents, setSearchResults, token = '') {
  let url = '/api/advanced_search';
  fetch(url, {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token,
    },
    method: "POST",
    body: JSON.stringify(whereComponents),
  })
  .then(response => response.json())
  .then(data => setSearchResults(data))
  .catch(data => console.error(data));
}